import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, ScrollView, ActivityIndicator, TextInput } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useUnistyles } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';

export default function PrinterSelectSheet({
  isVisible,
  printers,
  isDiscovering,
  onSelectPrinter,
  onDismiss,
  onRefresh,
  onConnectByIP,
  onConnectToDirectWifi,
  selectedPrinter,
}) {
  const { theme: { colors, fonts } } = useUnistyles();
  const [showIPInput, setShowIPInput] = useState(false);
  const [ipValue, setIpValue] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const [showWifiInput, setShowWifiInput] = useState(false);
  const [ssidValue, setSsidValue] = useState('DIRECT-');
  const [passphraseValue, setPassphraseValue] = useState('');
  const [isJoiningWifi, setIsJoiningWifi] = useState(false);

  // Reset fields fresh when an input section is opened. We intentionally do NOT
  // sync from `selectedPrinter` on every change — doing so clobbered the user's
  // edits whenever discovery re-saved the currently-selected printer, making it
  // impossible to switch to a new printer's creds (they kept reverting to the
  // saved printer's values). Wireless Direct starts blank for a new network;
  // IP pre-fills the last-used IP for convenience.
  const prevShowWifi = useRef(false);
  const prevShowIP = useRef(false);
  useEffect(() => {
    if (showWifiInput && !prevShowWifi.current) {
      setSsidValue('DIRECT-');
      setPassphraseValue('');
    }
    prevShowWifi.current = showWifiInput;
  }, [showWifiInput]);

  useEffect(() => {
    if (showIPInput && !prevShowIP.current) {
      setIpValue(selectedPrinter?.ipAddress || '');
    }
    prevShowIP.current = showIPInput;
  }, [showIPInput, selectedPrinter]);

  const handleConnectByIP = async () => {
    if (!ipValue.trim() || !onConnectByIP) return;
    setIsConnecting(true);
    try {
      await onConnectByIP(ipValue.trim(), ssidValue.trim(), passphraseValue.trim());
      setShowIPInput(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleJoinDirectWifi = async () => {
    if (!ssidValue.trim() || !onConnectToDirectWifi) return;
    setIsJoiningWifi(true);
    try {
      await onConnectToDirectWifi(ssidValue.trim(), passphraseValue.trim());
      setShowWifiInput(false);
    } finally {
      setIsJoiningWifi(false);
    }
  };

  return (
    <SheetModal
      isOpen={isVisible}
      title="Select Printer"
      cancelText="Close"
      onClose={onDismiss}
      onCancel={onDismiss}
    >
      <ScrollView keyboardShouldPersistTaps="handled" style={styles.container}>

        {/* Header row: status + refresh */}
        <View style={styles.statusRow}>
          {isDiscovering ? (
            <View style={styles.discoveringRow}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={[styles.discoveringText, { color: colors.textSecondary }]}>Discovering printers...</Text>
            </View>
          ) : (
            <Text style={[styles.discoveringText, { color: colors.textSecondary }]}>
              {printers.length > 0 ? `${printers.length} printer${printers.length > 1 ? 's' : ''} found` : 'No printers found on network'}
            </Text>
          )}
          <Pressable onPress={onRefresh} style={styles.refreshBtn} hitSlop={8}>
            <Unicon name="refresh" size={18} colorVariant="primary" />
          </Pressable>
        </View>

        {/* Discovered printers */}
        {printers.length > 0 && (
          <View style={[styles.section, { borderColor: colors.border }]}>
            {printers.map((item) => {
              const id = item.identifier || item.name;
              const isSelected = selectedPrinter?.identifier === id || selectedPrinter?.name === item.name;
              const isDymo = item.name?.toLowerCase().includes('dymo');
              return (
                <Pressable
                  key={id}
                  style={[
                    styles.printerRow,
                    { borderBottomColor: colors.border },
                    isSelected && { backgroundColor: colors.primaryLight || colors.backgroundSecondary },
                  ]}
                  onPress={() => onSelectPrinter(item)}
                >
                  <Unicon
                    name={isDymo ? 'usb' : (item.connectionType === 'bluetooth' ? 'bluetooth' : 'wifi')}
                    library={isDymo ? 'fa' : 'ionicon'}
                    size={20}
                    colorVariant={isSelected ? 'success' : 'primary'}
                  />
                  <View style={styles.printerInfo}>
                    <Text style={[styles.printerName, { color: colors.text, fontFamily: fonts.heading }]}>
                      {item.modelName || item.name}
                    </Text>
                    <Text style={[styles.printerSub, { color: colors.textSecondary }]}>
                      {isDymo ? 'USB / DYMO Connect' : (item.connectionType === 'bluetooth' ? item.macAddress : item.ipAddress)}
                    </Text>
                  </View>
                  {isSelected ? (
                    <Unicon name="checkmark-circle" size={18} colorVariant="success" />
                  ) : (
                    <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                  )}
                </Pressable>
              );
            })}
          </View>
        )}

        {/* Currently selected printer (if not in discovered list) */}
        {selectedPrinter && !printers.some(p =>
          (p.identifier || p.name) === (selectedPrinter.identifier || selectedPrinter.name)
        ) && (<>
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>SELECTED PRINTER</Text>
          <View style={[styles.section, { borderColor: colors.border }]}>
            <Pressable
              style={[styles.printerRow, { borderBottomColor: 'transparent', backgroundColor: colors.backgroundSecondary }]}
              onPress={() => {
                if (selectedPrinter.ipAddress && onConnectByIP) {
                  onConnectByIP(selectedPrinter.ipAddress, selectedPrinter.wifiSsid, selectedPrinter.wifiPassphrase);
                }
              }}
            >
              <Unicon
                name={selectedPrinter.name?.toLowerCase().includes('dymo') ? 'usb' : 'wifi'}
                size={20}
                colorVariant={selectedPrinter.isConnected ? 'success' : 'textSecondary'}
              />
              <View style={styles.printerInfo}>
                <Text style={[styles.printerName, { color: colors.text, fontFamily: fonts.heading }]}>
                  {selectedPrinter.modelName || selectedPrinter.name}
                </Text>
                <Text style={[styles.printerSub, { color: colors.textSecondary }]}>
                  {selectedPrinter.isConnected
                    ? (selectedPrinter.ipAddress ? `${selectedPrinter.ipAddress} · Connected` : 'Connected')
                    : (selectedPrinter.ipAddress ? `${selectedPrinter.ipAddress} · Tap to reconnect` : 'Disconnected · Tap to reconnect')}
                </Text>
              </View>
              {selectedPrinter.isConnected
                ? <Unicon name="checkmark" size={18} colorVariant="success" />
                : <Unicon name="refresh" size={18} colorVariant="primary" />
              }
            </Pressable>
          </View>
        </>)}

        {/* Divider */}
        <View style={[styles.divider, { backgroundColor: colors.border }]} />
        <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>CONNECTION OPTIONS</Text>

        {/* Wireless Direct */}
        {onConnectToDirectWifi && (
          <View style={[styles.section, { borderColor: colors.border }]}>
            {showWifiInput ? (
              <View style={styles.inputGroup}>
                <Text style={[styles.inputLabel, { color: colors.text }]}>Brother Wireless Direct Network</Text>
                <TextInput
                  style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}
                  placeholder="DIRECT-xxxxx"
                  placeholderTextColor={colors.textSecondary}
                  value={ssidValue}
                  onChangeText={setSsidValue}
                  autoCapitalize="none"
                  returnKeyType="next"
                />
                <TextInput
                  style={[styles.input, { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}
                  placeholder="Password (leave blank if none)"
                  placeholderTextColor={colors.textSecondary}
                  value={passphraseValue}
                  onChangeText={setPassphraseValue}
                  secureTextEntry
                  returnKeyType="go"
                  onSubmitEditing={handleJoinDirectWifi}
                />
                <View style={styles.btnRow}>
                  <Pressable style={[styles.secondaryBtn, { borderColor: colors.border }]} onPress={() => setShowWifiInput(false)}>
                    <Text style={[styles.secondaryBtnText, { color: colors.textSecondary }]}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.primaryBtn, { backgroundColor: colors.primary, flex: 1 }]}
                    onPress={handleJoinDirectWifi}
                    disabled={isJoiningWifi}
                  >
                    {isJoiningWifi
                      ? <ActivityIndicator size="small" color="#fff" />
                      : <Text style={styles.primaryBtnText}>Join & Discover</Text>
                    }
                  </Pressable>
                </View>
              </View>
            ) : (
              <Pressable style={styles.optionRow} onPress={() => setShowWifiInput(true)}>
                <View style={[styles.optionIconWrap, { backgroundColor: colors.backgroundSecondary }]}>
                  <Unicon name="wifi" size={16} colorVariant="primary" />
                </View>
                <View style={styles.optionInfo}>
                  <Text style={[styles.optionTitle, { color: colors.text }]}>Wireless Direct (DIRECT-xxxxx)</Text>
                  <Text style={[styles.optionSub, { color: colors.textSecondary }]}>Join the printer&apos;s own Wi-Fi network</Text>
                </View>
                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
              </Pressable>
            )}
          </View>
        )}

        {/* Manual IP */}
        {onConnectByIP && (
          <View style={[styles.section, { borderColor: colors.border }]}>
            {showIPInput ? (
              <View style={styles.inputGroup}>
                <Text style={[styles.inputLabel, { color: colors.text }]}>Printer IP Address</Text>
                <View style={styles.ipRow}>
                  <TextInput
                    style={[styles.input, styles.ipInput, { color: colors.text, borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}
                    placeholder="192.168.118.1"
                    placeholderTextColor={colors.textSecondary}
                    value={ipValue}
                    onChangeText={setIpValue}
                    keyboardType="decimal-pad"
                    autoFocus
                    returnKeyType="go"
                    onSubmitEditing={handleConnectByIP}
                  />
                  <Pressable
                    style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
                    onPress={handleConnectByIP}
                    disabled={isConnecting}
                  >
                    {isConnecting
                      ? <ActivityIndicator size="small" color="#fff" />
                      : <Text style={styles.primaryBtnText}>Connect</Text>
                    }
                  </Pressable>
                </View>
                <Pressable onPress={() => setShowIPInput(false)}>
                  <Text style={[styles.cancelText, { color: colors.textSecondary }]}>Cancel</Text>
                </Pressable>
              </View>
            ) : (
              <Pressable style={styles.optionRow} onPress={() => setShowIPInput(true)}>
                <View style={[styles.optionIconWrap, { backgroundColor: colors.backgroundSecondary }]}>
                  <Unicon name="print" size={16} colorVariant="primary" />
                </View>
                <View style={styles.optionInfo}>
                  <Text style={[styles.optionTitle, { color: colors.text }]}>Enter IP Address Manually</Text>
                  <Text style={[styles.optionSub, { color: colors.textSecondary }]}>
                    {selectedPrinter?.ipAddress ? `Last used: ${selectedPrinter.ipAddress}` : 'Fallback if discovery fails'}
                  </Text>
                </View>
                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
              </Pressable>
            )}
          </View>
        )}

        <View style={{ height: 24 }} />
      </ScrollView>
    </SheetModal>
  );
}

const styles = StyleSheet.create(() => ({
  container: {
    flex: 1,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  discoveringRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  discoveringText: {
    fontSize: 13,
  },
  refreshBtn: {
    padding: 4,
  },
  section: {
    borderWidth: 1,
    borderRadius: 10,
    marginBottom: 12,
    overflow: 'hidden',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.5,
    marginBottom: 8,
    marginTop: 4,
    paddingHorizontal: 4,
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  printerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  printerInfo: { flex: 1 },
  printerName: { fontSize: 15 },
  printerSub: { fontSize: 12, marginTop: 2 },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 14,
  },
  optionIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionInfo: { flex: 1 },
  optionTitle: { fontSize: 14, fontWeight: '500' },
  optionSub: { fontSize: 12, marginTop: 2 },
  inputGroup: {
    padding: 14,
    gap: 10,
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 2,
  },
  input: {
    height: 42,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  ipRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  ipInput: {
    flex: 1,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  primaryBtn: {
    height: 42,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  secondaryBtn: {
    height: 42,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  secondaryBtnText: {
    fontSize: 14,
  },
  cancelText: {
    fontSize: 13,
    textAlign: 'center',
    paddingTop: 4,
  },
}));
